import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  requestCreateReaction,
  requestDeletePost,
  requestRetrieveChat,
  requestViewComments,
} from '../../../api/market-buzz/market-buzz.actions';
import { setPost } from '../../../slices/market-buzz/market-buzz.slice';

import Post from '../../../components/common/post/Post';

const PostContainer = ({ post, reactions, adminRole }) => {
  const [showAllComments, setShowAllComments] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const fetchingComments = useRef(false);

  useEffect(() => {
    if (!post?.externalReference || fetchingComments.current) return;

    fetchingComments.current = true;
    dispatch(
      requestViewComments({
        postExternalReference: post.externalReference,
        page: 0,
        size: 2,
        onSuccess: () => (fetchingComments.current = false),
      })
    );
  }, [dispatch, post]);

  const createReaction = (reactionType, onError) => {
    const reaction = {
      reaction: reactionType,
      parentPostExternalReference: post.externalReference,
    };

    dispatch(requestCreateReaction({ reaction, onError }));
  };

  const deleteReaction = (reaction, onError) => {
    dispatch(requestDeletePost({ postExternalReference: reaction.externalReference, reaction: true, onError }));
  };

  const deletePost = (postExternalReference) => {
    dispatch(requestDeletePost({ postExternalReference }));
  };

  const retrieveChat = (postCreatorExternalReference) => {
    dispatch(
      requestRetrieveChat({
        postCreatorExternalReference,
        onSuccess: (chatExternalReference) => navigate(`/market/direct-messages/${chatExternalReference}`),
      })
    );
  };

  const viewAllComments = () => {
    fetchingComments.current = true;
    dispatch(
      requestViewComments({
        postExternalReference: post.externalReference,
        page: 0,
        size: 999,
        onSuccess: () => (fetchingComments.current = false),
      })
    );
    setShowAllComments(true);
  };

  const navigateToEditPost = () => {
    dispatch(setPost(post));
    navigate('/market/market-buzz/edit');
  };

  const navigateToProduct = () => {
    if (post.productType === 'seller') {
      navigate(`/market/product-listing/${post.productExternalReference}`);
    } else if (post.productType === 'buyer') {
      navigate(`/market/buyer-listing/${post.productExternalReference}`);
    }
  };

  return (
    <Post
      post={post}
      reactions={reactions}
      adminRole={adminRole}
      showAllComments={showAllComments}
      createReaction={createReaction}
      deleteReaction={deleteReaction}
      deletePost={deletePost}
      retrieveChat={retrieveChat}
      viewAllComments={viewAllComments}
      navigateToEditPost={navigateToEditPost}
      navigateToProduct={navigateToProduct}
    />
  );
};

PostContainer.propTypes = {
  post: PropTypes.shape({}).isRequired,
  reactions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  adminRole: PropTypes.bool,
};

PostContainer.defaultProps = {
  adminRole: false,
};

export default PostContainer;
